import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { message } from 'antd';
import { uniq } from 'lodash';
import { useCouponFormStore } from '../../store/couponFormStore';
import { CouponApiV2 } from '../../lib/api/coupon/CouponApiV2';
import { couponActions } from '../../store/modules/coupon';
import { appActions } from '../../store/modules/app';
import { useCreateCouponMutation } from '../queries/coupon/useCreateCouponMutation';
import { useEditCouponMutation } from '../queries/coupon/useEditCouponMutation';
import toastMsg from '../../lib/toastMsg';
import couponToastMsg from '../../constants/service/toastMsg/couponToastMsg';
import { setDateFormat } from '../../lib/moment';
import { setApplyCouponDiscount, setCouponGenerateLimitType } from '../../lib/utils/couponUtils';
import { asyncSimpleModal, setIfNull, excelDownLoad, _setResponseListForm, setUncheckableTableTransferList } from '../../lib/common';
import { COUPON_INFO_MODEL } from '../../store/modules/coupon/models';
import {
  COUPON_AVAILABLE_TYPE,
  COUPON_BRAND_LINK_INFO_TAB_INDEX,
  COUPON_DISCOUNT_TYPE,
  COUPON_GENERATE_LIMIT_TYPE,
  COUPON_INFO_TAB_INDEX,
  COUPON_PERIOD_TYPE,
  COUPON_PRODUCT_LINK_INFO_TAB_INDEX,
} from '../../constants/service/app/couponConstants';
import { DATE_FORMAT, NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT } from '../../constants/service/common/utilConstants';
import {
  COUPON_PRODUCT_ITEM_LIST_FILTER_OPTION_MODEL,
  COUPON_PRODUCT_ITEM_LIST_SORT_OPTION_MODEL,
  COUPON_BRAND_ITEM_LIST_FILTER_OPTION_MODEL,
} from '../../store/modules/coupon/models';
import { _LIST_PAGINATION_MODEL } from '../../store/modules/app/models';

type CouponFormMode = 'create' | 'edit' | 'copy';

export const useCouponForm = (mode: CouponFormMode, couponId?: number) => {
  const store = useCouponFormStore();
  const dispatch = useDispatch();
  const setData = store.setData;
  const createMutation = useCreateCouponMutation();
  const editMutation = useEditCouponMutation();

  const toggleModal = useCallback((payload: Record<string, unknown>) => {
    dispatch(couponActions.toggleModal(payload));
  }, [dispatch]);

  const getProductSubCategoryList = useCallback((payload: Record<string, unknown>) => {
    dispatch(appActions.getProductSubCategoryList(payload));
  }, [dispatch]);

  /**
   * edit/copy 모드에서 쿠폰 정보 로드
   */
  useEffect(() => {
    useCouponFormStore.setState({ _active: true });
    if ((mode === 'edit' || mode === 'copy') && couponId) {
      loadCouponInfo(couponId);
    }
    return () => {
      store.reset();
    };
  }, [couponId, mode]);

  const loadCouponInfo = async (id: number) => {
    try {
      const payload = {
        couponId: id,
        couponProductLink: {
          filterOption: COUPON_PRODUCT_ITEM_LIST_FILTER_OPTION_MODEL,
          sortOption: COUPON_PRODUCT_ITEM_LIST_SORT_OPTION_MODEL,
          pagination: _LIST_PAGINATION_MODEL,
        },
        couponBrandLink: {
          filterOption: COUPON_BRAND_ITEM_LIST_FILTER_OPTION_MODEL,
          pagination: _LIST_PAGINATION_MODEL,
        },
      };
      const result = await CouponApiV2.getCouponInfo(payload);
      if ((result as any).msg) {
        toastMsg.error((result as any).msg);
        return;
      }
      toastMsg.success(couponToastMsg.GET_COUPON_INFO_REQUEST_SUCCESS);

      const data: any = result.data;

      // 레거시 reducer 변환 재현: generateLimitType 계산
      const couponInfo = {
        ...data.couponInfo,
        generateLimitType: setCouponGenerateLimitType(data.couponInfo.generateLimitDay),
      };

      // copy 모드: 원본 쿠폰 코드 제거
      if (mode === 'copy') {
        couponInfo.code = null;
      }

      const newState: Record<string, unknown> = {
        couponInfo,
      };

      if (data.couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_PRODUCT_COUPON) {
        // 레거시 reducer 변환 재현: itemList에 uncheckable 플래그 설정
        const itemListFormatted = _setResponseListForm(
          data.couponProductLinkInfo.itemList.list,
          data.couponProductLinkInfo.totalCnt,
          payload.couponProductLink,
        );
        const selectedIdList = (data.couponProductLinkInfo.selectedItemList?.list ?? []).map((item: any) => item.id);
        itemListFormatted.list = setUncheckableTableTransferList({
          list: itemListFormatted.list,
          selectedIdList,
        });

        // 레거시 reducer 변환 재현: selectedItemList에 setApplyCouponDiscount 적용 (edit 모드)
        let selectedItemList = data.couponProductLinkInfo.selectedItemList;
        if (mode === 'edit' && selectedItemList?.list && couponInfo.discountType != null && couponInfo.discountAmount != null) {
          selectedItemList = {
            ...selectedItemList,
            list: setApplyCouponDiscount(selectedItemList.list, couponInfo.discountType, couponInfo.discountAmount),
          };
        }

        newState.couponProductLinkInfo = {
          itemList: itemListFormatted,
          selectedItemList,
          selectedItemBrandCount: data.couponProductLinkInfo.selectedItemBrandCount ?? 0,
        };
        newState.mainProductCategorySelectList = data.mainProductCategorySelectList;
        newState.subProductCategorySelectList = data.subProductCategorySelectList;
      } else if (data.couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_BRAND_COUPON) {
        // 레거시 reducer 변환 재현: itemList에 uncheckable 플래그 설정
        const itemListFormatted = _setResponseListForm(
          data.couponBrandLinkInfo.itemList.list,
          data.couponBrandLinkInfo.totalCnt,
          payload.couponBrandLink,
        );
        const selectedIdList = (data.couponBrandLinkInfo.selectedItemList?.list ?? []).map((item: any) => item.id);
        itemListFormatted.list = setUncheckableTableTransferList({
          list: itemListFormatted.list,
          selectedIdList,
        });

        newState.couponBrandLinkInfo = {
          itemList: itemListFormatted,
          selectedItemList: data.couponBrandLinkInfo.selectedItemList,
        };
      }

      store.setState(newState as any);
    } catch (error) {
      toastMsg.error(couponToastMsg.GET_COUPON_INFO_REQUEST_ERROR, error);
    }
  };

  /**
   * 쿠폰 생성/편집 제출
   */
  const handleSubmit = useCallback(
    (form: any) => {
      const { couponInfo, couponProductLinkInfo, couponBrandLinkInfo } = useCouponFormStore.getState();

      if (
        !couponInfo.name ||
        !couponInfo.description ||
        !couponInfo.minimumPurchasePrice ||
        !couponInfo.generateLimitCount ||
        !couponInfo.generateLimitDay
      ) {
        setData({ path: ['couponCreate', 'currentTabKey'], value: COUPON_INFO_TAB_INDEX });
      }

      if (
        couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_PRODUCT_COUPON &&
        couponProductLinkInfo.selectedItemList.idList.length === 0
      ) {
        setData({ path: ['couponCreate', 'currentTabKey'], value: COUPON_PRODUCT_LINK_INFO_TAB_INDEX });
        message.error('선택된 쿠폰 사용 가능한 상품이 없습니다.');
        return;
      } else if (
        couponInfo.availableType === COUPON_AVAILABLE_TYPE.TARGET_BRAND_COUPON &&
        couponBrandLinkInfo.selectedItemList.idList.length === 0
      ) {
        setData({ path: ['couponCreate', 'currentTabKey'], value: COUPON_BRAND_LINK_INFO_TAB_INDEX });
        message.error('선택된 쿠폰 사용 가능한 브랜드가 없습니다.');
        return;
      }

      form.validateFieldsAndScroll({ scroll: { offsetBottom: 150, offsetTop: 150 } }, (err: any) => {
        if (err) return;

        let generateLimitCount = couponInfo.generateLimitCount;
        let generateLimitDay = couponInfo.generateLimitDay;
        let dayPlus: number | null = null;
        let startDate: string | null = null;
        let endDate: string | null = null;

        if (couponInfo.periodType === COUPON_PERIOD_TYPE.START_AND_END_DATE) {
          if (!couponInfo.period.startDate || !couponInfo.period.endDate) {
            message.error('쿠폰 시작일과 종료일을 설정해주세요.');
            return;
          }
          startDate = setDateFormat(couponInfo.period.startDate, DATE_FORMAT.FULL_DASH_FORMAT);
          endDate = setDateFormat(couponInfo.period.endDate, DATE_FORMAT.FULL_DASH_FORMAT);
        } else if (couponInfo.periodType === COUPON_PERIOD_TYPE.DAY_PLUS) {
          dayPlus = couponInfo.dayPlus;
        }

        if (couponInfo.generateLimitType === COUPON_GENERATE_LIMIT_TYPE.MULTIPLE_FOREVER) {
          generateLimitDay = 0;
        } else if (couponInfo.generateLimitType === COUPON_GENERATE_LIMIT_TYPE.UNLIMIT) {
          generateLimitDay = generateLimitCount = null;
        }

        const submitPayload = {
          couponId: couponInfo.couponId,
          couponInfo: {
            name: couponInfo.name,
            availableType: couponInfo.availableType,
            code: setIfNull(couponInfo.code),
            periodType: couponInfo.periodType,
            discountType: couponInfo.discountType,
            discountAmount: couponInfo.discountAmount,
            minimumPurchasePrice: couponInfo.minimumPurchasePrice,
            maxDiscountPrice:
              couponInfo.discountType === COUPON_DISCOUNT_TYPE.RATE ? couponInfo.maxDiscountPrice : null,
            exclude24k: couponInfo.exclude24k,
            description: setIfNull(couponInfo.description),
            startDate,
            endDate,
            dayPlus,
            memo: setIfNull(couponInfo.memo),
            generateLimitCount,
            generateLimitDay,
            couponProductItemIdList: couponProductLinkInfo.selectedItemList.idList,
            couponBrandItemIdList: couponBrandLinkInfo.selectedItemList.idList,
            status: couponInfo.status,
            isDownloadable: couponInfo.isDownloadable,
            settlementShare: couponInfo.settlementShare,
          },
        };

        if (mode === 'edit') {
          editMutation.mutate(submitPayload);
        } else {
          createMutation.mutate(submitPayload);
        }
      });
    },
    [mode, setData, createMutation, editMutation],
  );

  /**
   * 쿠폰 유형 변경하기
   */
  const handleAvailableTypeChange = useCallback(
    async (availableType: number) => {
      const { couponInfo, couponProductLinkInfo, couponBrandLinkInfo } = useCouponFormStore.getState();
      const selectedProductListLength = couponProductLinkInfo.selectedItemList.idList.length;
      const selectedBrandListLength = couponBrandLinkInfo.selectedItemList.idList.length;

      if (couponInfo.availableType !== availableType) {
        if (selectedProductListLength || selectedBrandListLength) {
          const confirmResult = await asyncSimpleModal({
            type: 'confirm',
            title: '쿠폰 유형 변경하기',
            message: '상품, 브랜드 리스트 아이템이 초기화됩니다. 진행하시겠습니까?',
          });
          if (!confirmResult) return;
        }

        setData({ path: ['couponCreate', 'couponInfo', 'availableType'], value: availableType });

        if (availableType === COUPON_AVAILABLE_TYPE.TARGET_BRAND_COUPON) {
          setData({ path: ['couponCreate', 'couponInfo', 'exclude24k'], value: true });
        } else {
          setData({ path: ['couponCreate', 'couponInfo', 'exclude24k'], value: false });
        }

        resetRelatedItems();
      }
    },
    [setData],
  );

  /**
   * 24k 제외하기
   */
  const setExclude24k = useCallback(
    (isExclude: boolean) => {
      setData({ path: ['couponCreate', 'couponInfo', 'exclude24k'], value: isExclude });
      resetRelatedItems();
    },
    [setData],
  );

  /**
   * 쿠폰 기간 타입 변경하기
   */
  const handlePeriodTypeChange = useCallback(
    (periodType: number, form: any) => {
      setData({ path: ['couponCreate', 'couponInfo', 'dayPlus'], value: COUPON_INFO_MODEL.dayPlus });
      setTimeout(() => {
        form.setFieldsValue({ dayPlus: COUPON_INFO_MODEL.dayPlus });
      }, 0);
      setData({ path: ['couponCreate', 'couponInfo', 'periodType'], value: periodType });
    },
    [setData],
  );

  /**
   * 제한 횟수 타입 변경하기
   */
  const handleGenerateLimitChange = useCallback(
    (generateLimitType: number, form: any) => {
      setData({
        path: ['couponCreate', 'couponInfo', 'generateLimitDay'],
        value: COUPON_INFO_MODEL.generateLimitDay,
      });
      setData({
        path: ['couponCreate', 'couponInfo', 'generateLimitCount'],
        value: COUPON_INFO_MODEL.generateLimitCount,
      });
      setTimeout(() => {
        form.setFieldsValue({
          generateLimitDay: COUPON_INFO_MODEL.generateLimitDay,
          generateLimitCount: COUPON_INFO_MODEL.generateLimitCount,
        });
      }, 0);
      setData({
        path: ['couponCreate', 'couponInfo', 'generateLimitType'],
        value: generateLimitType,
      });
    },
    [setData],
  );

  /**
   * 쿠폰 적용 가능 상품 아이템 리스트 불러오기
   */
  const getCouponProductItemList = useCallback(
    async (option: any) => {
      const { couponProductLinkInfo, couponInfo } = useCouponFormStore.getState();
      const { filterOption, sortOption, pagination } = couponProductLinkInfo.itemList;
      const { minimumPurchasePrice, discountType, discountAmount, maxDiscountPrice } = couponInfo;

      const { isSelectedProductClear, ...apiOption } = option;

      try {
        const result = await CouponApiV2.getCouponProductItemList({
          sortOption,
          pagination,
          minimumPurchasePrice,
          discountType,
          discountAmount,
          maxDiscountPrice,
          ...apiOption,
          filterOption: { ...filterOption, ...(apiOption.filterOption || {}) },
        });
        if (result.msg) {
          toastMsg.error(result.msg);
        } else {
          toastMsg.success(couponToastMsg.GET_COUPON_PRODUCT_ITEM_REQUEST_SUCCESS);
          const mergedOption = {
            filterOption: { ...filterOption, ...(apiOption.filterOption || {}) },
            sortOption: apiOption.sortOption ?? sortOption,
            pagination: apiOption.pagination ?? pagination,
          };
          const data = (_setResponseListForm as any)(
            result.data.couponProductLinkInfo.list,
            result.data.couponProductLinkInfo.totalCnt,
            mergedOption,
          );

          const storeState = useCouponFormStore.getState();
          const newProductLinkInfo = {
            ...storeState.couponProductLinkInfo,
            itemList: data,
          };
          if (isSelectedProductClear) {
            newProductLinkInfo.selectedItemList = { list: [], idList: [] };
            newProductLinkInfo.selectedItemBrandCount = 0;
          }
          store.setState({
            couponProductLinkInfo: newProductLinkInfo,
            mainProductCategorySelectList: result.data.mainProductCategorySelectList as any,
            subProductCategorySelectList: result.data.subProductCategorySelectList as any,
          });
        }
      } catch (error) {
        toastMsg.error(couponToastMsg.GET_COUPON_PRODUCT_ITEM_REQUEST_ERROR, error);
      }
    },
    [],
  );

  /**
   * 쿠폰 적용 가능 브랜드 아이템 리스트 불러오기
   */
  const getCouponBrandItemList = useCallback(
    async (option: any) => {
      const { couponBrandLinkInfo, couponInfo } = useCouponFormStore.getState();
      const { filterOption, pagination } = couponBrandLinkInfo.itemList;
      const { minimumPurchasePrice, discountType, discountAmount, maxDiscountPrice, exclude24k } = couponInfo;

      const { isSelectedBrandClear, ...apiOption } = option;

      try {
        const result = await CouponApiV2.getCouponBrandItemList({
          pagination,
          minimumPurchasePrice,
          discountType,
          discountAmount,
          maxDiscountPrice,
          exclude24k,
          ...apiOption,
          filterOption: { ...filterOption, ...(apiOption.filterOption || {}) },
        });
        if (result.msg) {
          toastMsg.error(result.msg);
        } else {
          toastMsg.success(couponToastMsg.GET_COUPON_BRAND_ITEM_REQUEST_SUCCESS);
          const mergedOption = {
            filterOption: { ...filterOption, ...(apiOption.filterOption || {}) },
            pagination: apiOption.pagination ?? pagination,
          };
          const data = (_setResponseListForm as any)(
            result.data.couponBrandLinkInfo.list,
            result.data.couponBrandLinkInfo.totalCnt,
            mergedOption,
          );

          const storeState = useCouponFormStore.getState();
          const newBrandLinkInfo = {
            ...storeState.couponBrandLinkInfo,
            itemList: data,
          };
          if (isSelectedBrandClear) {
            newBrandLinkInfo.selectedItemList = { list: [], idList: [] };
          }
          store.setState({ couponBrandLinkInfo: newBrandLinkInfo });
        }
      } catch (error) {
        toastMsg.error(couponToastMsg.GET_COUPON_BRAND_ITEM_REQUEST_ERROR, error);
      }
    },
    [],
  );

  /**
   * 상품 아이템 선택/해제
   */
  const selectCouponProductItemList = useCallback(
    (action: 'add' | 'remove', moveIdList: number[]) => {
      const { couponProductLinkInfo, couponInfo } = useCouponFormStore.getState();
      const { itemList, selectedItemList } = couponProductLinkInfo;
      const { discountType, discountAmount } = couponInfo;

      let updatedSelectList: any[] = [];

      if (action === 'add') {
        let targetList = itemList.list.filter((item: any) => moveIdList.includes(item.id));
        targetList = setApplyCouponDiscount(targetList, discountType, discountAmount);
        updatedSelectList = [...targetList, ...selectedItemList.list];
      } else if (action === 'remove') {
        updatedSelectList = selectedItemList.list.filter((item: any) => !moveIdList.includes(item.id));
      }

      const updatedItemList = itemList.list.map((item: any) => ({
        ...item,
        uncheckable: moveIdList.includes(item.id) ? action === 'add' : item.uncheckable,
      }));

      const selectedItemStoreIdList = updatedSelectList.map((item: any) => item.storeId);

      store.setState({
        couponProductLinkInfo: {
          ...useCouponFormStore.getState().couponProductLinkInfo,
          itemList: { ...useCouponFormStore.getState().couponProductLinkInfo.itemList, list: updatedItemList },
          selectedItemList: {
            list: updatedSelectList,
            idList: updatedSelectList.map((item: any) => item.id),
          },
          selectedItemBrandCount: uniq(selectedItemStoreIdList).length,
        },
      });
    },
    [],
  );

  /**
   * 브랜드 아이템 선택/해제
   */
  const selectCouponBrandItemList = useCallback(
    (action: 'add' | 'remove', moveIdList: number[]) => {
      const { couponBrandLinkInfo } = useCouponFormStore.getState();
      const { itemList, selectedItemList } = couponBrandLinkInfo;

      let updatedSelectList: any[] = [];

      if (action === 'add') {
        const targetList = itemList.list.filter((item: any) => moveIdList.includes(item.id));
        updatedSelectList = [...targetList, ...selectedItemList.list];
      } else if (action === 'remove') {
        updatedSelectList = selectedItemList.list.filter((item: any) => !moveIdList.includes(item.id));
      }

      const updatedItemList = itemList.list.map((item: any) => ({
        ...item,
        uncheckable: moveIdList.includes(item.id) ? action === 'add' : item.uncheckable,
      }));

      store.setState({
        couponBrandLinkInfo: {
          ...useCouponFormStore.getState().couponBrandLinkInfo,
          itemList: { ...useCouponFormStore.getState().couponBrandLinkInfo.itemList, list: updatedItemList },
          selectedItemList: {
            list: updatedSelectList,
            idList: updatedSelectList.map((item: any) => item.id),
          },
        },
      });
    },
    [],
  );

  /**
   * 전체 상품 리스트 엑셀 다운로드
   */
  const getCouponProductItemListExcelDownload = useCallback(async () => {
    const { couponProductLinkInfo, couponInfo } = useCouponFormStore.getState();
    const { filterOption, sortOption } = couponProductLinkInfo.itemList;
    try {
      const result = await CouponApiV2.getCouponProductItemListExcelDownload({
        filterOption: { ...filterOption, minimumPurchasePrice: couponInfo.minimumPurchasePrice },
        sortOption,
        discountType: couponInfo.discountType,
        discountAmount: couponInfo.discountAmount,
        maxDiscountPrice: couponInfo.maxDiscountPrice,
      });
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(couponToastMsg.GET_COUPON_PRODUCT_ITEM_LIST_EXCEL_DOWNLOAD_REQUEST_SUCCESS);
        excelDownLoad('상품 아이템 리스트', result.data.couponProductItemExcelSheet);
      }
    } catch (error: any) {
      toastMsg.error(error.message || couponToastMsg.GET_COUPON_PRODUCT_ITEM_LIST_EXCEL_DOWNLOAD_REQUEST_ERROR, error);
    }
  }, []);

  /**
   * 전체 브랜드 리스트 엑셀 다운로드
   */
  const getCouponBrandItemListExcelDownload = useCallback(async () => {
    const { couponBrandLinkInfo, couponInfo } = useCouponFormStore.getState();
    const { filterOption } = couponBrandLinkInfo.itemList;
    try {
      const result = await CouponApiV2.getCouponBrandItemListExcelDownload({
        filterOption,
        minimumPurchasePrice: couponInfo.minimumPurchasePrice,
        discountType: couponInfo.discountType,
        discountAmount: couponInfo.discountAmount,
        maxDiscountPrice: couponInfo.maxDiscountPrice,
        exclude24k: couponInfo.exclude24k,
      });
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(couponToastMsg.GET_COUPON_BRAND_ITEM_LIST_EXCEL_DOWNLOAD_REQUEST_SUCCESS);
        excelDownLoad('브랜드 아이템 리스트', result.data.couponBrandItemExcelSheet);
      }
    } catch (error: any) {
      toastMsg.error(error.message || couponToastMsg.GET_COUPON_BRAND_ITEM_LIST_EXCEL_DOWNLOAD_REQUEST_ERROR, error);
    }
  }, []);

  /**
   * 선택된 상품 리스트 엑셀 다운로드
   */
  const downloadSelectedCouponProductItemListExcel = useCallback(async () => {
    const { couponProductLinkInfo, couponInfo } = useCouponFormStore.getState();
    const { idList } = couponProductLinkInfo.selectedItemList;
    if (!idList.length) {
      message.error(NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT);
      return;
    }
    try {
      const result = await CouponApiV2.getSelectedCouponProductItemListExcelDownload({
        idList,
        discountType: couponInfo.discountType,
        discountAmount: couponInfo.discountAmount,
        maxDiscountPrice: couponInfo.maxDiscountPrice,
      });
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(couponToastMsg.GET_SELECTED_COUPON_PRODUCT_ITEM_LIST_EXCEL_DOWNLOAD_REQUEST_SUCCESS);
        excelDownLoad('선택 상품 아이템 리스트', result.data.selectedCouponProductItemExcelSheet);
      }
    } catch (error: any) {
      toastMsg.error(
        error.message || couponToastMsg.GET_SELECTED_COUPON_PRODUCT_ITEM_LIST_EXCEL_DOWNLOAD_REQUEST_ERROR,
        error,
      );
    }
  }, []);

  /**
   * 선택된 브랜드 리스트 엑셀 다운로드
   */
  const downloadSelectedCouponBrandItemListExcel = useCallback(async () => {
    const { couponBrandLinkInfo, couponInfo } = useCouponFormStore.getState();
    const { idList } = couponBrandLinkInfo.selectedItemList;
    if (!idList.length) {
      message.error(NO_DATA_ON_EXCEL_DOWNLOAD_WARNING_TEXT);
      return;
    }
    try {
      const result = await CouponApiV2.getSelectedCouponBrandItemListExcelDownload({
        idList,
        minimumPurchasePrice: couponInfo.minimumPurchasePrice,
        discountType: couponInfo.discountType,
        discountAmount: couponInfo.discountAmount,
        maxDiscountPrice: couponInfo.maxDiscountPrice,
        exclude24k: couponInfo.exclude24k,
      });
      if (result.msg) {
        toastMsg.error(result.msg);
      } else {
        toastMsg.success(couponToastMsg.GET_SELECTED_COUPON_BRAND_ITEM_LIST_EXCEL_DOWNLOAD_REQUEST_SUCCESS);
        excelDownLoad('선택 브랜드 아이템 리스트', result.data.selectedCouponBrandItemExcelSheet);
      }
    } catch (error: any) {
      toastMsg.error(
        error.message || couponToastMsg.GET_SELECTED_COUPON_BRAND_ITEM_LIST_EXCEL_DOWNLOAD_REQUEST_ERROR,
        error,
      );
    }
  }, []);

  /**
   * 상품 리스트 엑셀 업로드 유효성 체크
   */
  const checkValidCouponProductItemListExcelUpload = useCallback((file: File) => {
    const { couponInfo } = useCouponFormStore.getState();
    CouponApiV2.checkValidCouponProductItemListExcelUpload({ attachment: [file] })
      .then((result) => {
        if ((result as any).msg) {
          toastMsg.error((result as any).msg);
        } else {
          toastMsg.success(couponToastMsg.CHECK_VALID_COUPON_PRODUCT_ITEM_LIST_EXCEL_UPLOAD_REQUEST_SUCCESS);

          const { requestCnt, possibleCnt, impossibleCnt } = result.data as any;
          let { productList } = result.data as any;

          if (impossibleCnt > 0) {
            dispatch(couponActions.toggleModal({
              name: 'excelUploadCouponProductValidFailModal',
              value: true,
              data: { productList, requestCnt, possibleCnt, impossibleCnt },
            }));
          } else {
            productList = setApplyCouponDiscount(productList, couponInfo.discountType, couponInfo.discountAmount);
            dispatch(couponActions.toggleModal({
              name: 'excelUploadCouponProductValidSuccessModal',
              value: true,
              data: { productList, requestCnt, possibleCnt, impossibleCnt },
            }));
          }
        }
      })
      .catch((error) => {
        toastMsg.error(couponToastMsg.CHECK_VALID_COUPON_PRODUCT_ITEM_LIST_EXCEL_UPLOAD_REQUEST_ERROR, error);
      });
  }, [dispatch]);

  /**
   * 브랜드 리스트 엑셀 업로드 유효성 체크
   */
  const checkValidCouponBrandItemListExcelUpload = useCallback((file: File) => {
    const { couponInfo } = useCouponFormStore.getState();
    CouponApiV2.checkValidCouponBrandItemListExcelUpload({
      attachment: [file],
      minimumPurchasePrice: couponInfo.minimumPurchasePrice,
      exclude24k: couponInfo.exclude24k,
    })
      .then((result) => {
        if ((result as any).msg) {
          toastMsg.error((result as any).msg);
        } else {
          toastMsg.success(couponToastMsg.CHECK_VALID_COUPON_BRAND_ITEM_LIST_EXCEL_UPLOAD_REQUEST_SUCCESS);

          const { brandList, requestCnt, possibleCnt, impossibleCnt } = result.data as any;

          if (impossibleCnt > 0) {
            dispatch(couponActions.toggleModal({
              name: 'excelUploadCouponBrandValidFailModal',
              value: true,
              data: { brandList, requestCnt, possibleCnt, impossibleCnt },
            }));
          } else {
            dispatch(couponActions.toggleModal({
              name: 'excelUploadCouponBrandValidSuccessModal',
              value: true,
              data: { brandList, requestCnt, possibleCnt, impossibleCnt },
            }));
          }
        }
      })
      .catch((error) => {
        toastMsg.error(couponToastMsg.CHECK_VALID_COUPON_BRAND_ITEM_LIST_EXCEL_UPLOAD_REQUEST_ERROR, error);
      });
  }, [dispatch]);

  /**
   * 상품/브랜드 연결 초기화
   */
  const resetRelatedItems = useCallback(() => {
    const state = useCouponFormStore.getState();
    store.setState({
      couponProductLinkInfo: {
        ...state.couponProductLinkInfo,
        itemList: { ...state.couponProductLinkInfo.itemList, list: [] },
        selectedItemList: { list: [], idList: [] },
        selectedItemBrandCount: 0,
      },
      couponBrandLinkInfo: {
        ...state.couponBrandLinkInfo,
        itemList: { ...state.couponBrandLinkInfo.itemList, list: [] },
        selectedItemList: { list: [], idList: [] },
      },
    });
  }, []);

  return {
    couponCreate: store,
    setData,
    toggleModal,
    getProductSubCategoryList,
    handleSubmit,
    handleAvailableTypeChange,
    handlePeriodTypeChange,
    handleGenerateLimitChange,
    setExclude24k,
    getCouponProductItemList,
    getCouponBrandItemList,
    selectCouponProductItemList,
    selectCouponBrandItemList,
    getCouponProductItemListExcelDownload,
    getCouponBrandItemListExcelDownload,
    downloadSelectedCouponProductItemListExcel,
    downloadSelectedCouponBrandItemListExcel,
    checkValidCouponProductItemListExcelUpload,
    checkValidCouponBrandItemListExcelUpload,
  };
};
